'use client'

import { forwardRef, ReactNode, useState, useMemo } from 'react'
import { ChevronUp, ChevronDown, Search, Filter, MoreHorizontal, AlertTriangle } from 'lucide-react'

/**
 * BIRHAUS Table Component
 * 
 * Enhanced table implementing BIRHAUS Principle #1: Miller's Law (7±2)
 * 
 * Features:
 * - Maximum 7 visible columns (cognitive load optimization)
 * - Progressive disclosure for additional columns
 * - Spanish-first labeling for financial data
 * - Sortable and filterable with cognitive load warnings
 * - Accessibility-first design (WCAG AA+)
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export interface BirhausTableColumn<T = any> {
  id: string
  header: string
  headerEs?: string
  headerEn?: string
  accessor: keyof T | ((item: T) => any)
  
  // Column behavior
  sortable?: boolean
  filterable?: boolean
  searchable?: boolean
  
  // Formatting
  cell?: (value: any, item: T) => ReactNode
  width?: string | number
  align?: 'left' | 'center' | 'right'
  
  // Financial formatting
  isCurrency?: boolean
  isPercentage?: boolean
  isDate?: boolean
  
  // Visibility (for progressive disclosure)
  priority?: 'high' | 'medium' | 'low'
  hideOnMobile?: boolean
  
  // Cognitive load hints
  cognitiveWeight?: number // 1-10 scale
}

export interface BirhausTableProps<T = any> {
  // Data
  data: T[]
  columns: BirhausTableColumn<T>[]
  
  // BIRHAUS Principle #7: Bilingual by design
  emptyMessage?: string
  emptyMessageEs?: string
  emptyMessageEn?: string
  
  loadingMessage?: string
  loadingMessageEs?: string
  loadingMessageEn?: string
  
  // Miller's Law enforcement
  maxVisibleColumns?: number
  showCognitiveWarning?: boolean
  
  // Functionality
  sortable?: boolean
  filterable?: boolean
  searchable?: boolean
  selectable?: boolean
  
  // State
  loading?: boolean
  selectedRows?: Set<string | number>
  onRowSelect?: (id: string | number, selected: boolean) => void
  onRowsSelect?: (ids: (string | number)[], selected: boolean) => void
  
  // Pagination
  pagination?: {
    page: number
    pageSize: number
    total: number
    onPageChange: (page: number) => void
    onPageSizeChange: (size: number) => void
  }
  
  // Sorting
  sortConfig?: {
    key: string
    direction: 'asc' | 'desc'
  }
  onSort?: (key: string, direction: 'asc' | 'desc') => void
  
  // Filtering
  filters?: Record<string, any>
  onFilter?: (filters: Record<string, any>) => void
  
  // Search
  searchValue?: string
  onSearch?: (value: string) => void
  
  // Row identification
  getRowId?: (item: T, index: number) => string | number
  
  // Actions
  rowActions?: Array<{
    id: string
    label: string
    labelEs?: string
    labelEn?: string
    icon?: ReactNode
    onClick: (item: T) => void
    variant?: 'primary' | 'secondary' | 'destructive'
    disabled?: (item: T) => boolean
  }>
  
  // Styling
  variant?: 'default' | 'striped' | 'bordered' | 'minimal'
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export const BirhausTable = forwardRef<HTMLTableElement, BirhausTableProps>(({
  // Data
  data,
  columns,
  
  // Messages (Spanish-first)
  emptyMessage,
  emptyMessageEs,
  emptyMessageEn,
  loadingMessage,
  loadingMessageEs,
  loadingMessageEn,
  
  // Miller's Law settings
  maxVisibleColumns = 7,
  showCognitiveWarning = true,
  
  // Functionality
  sortable = true,
  filterable = false,
  searchable = false,
  selectable = false,
  
  // State
  loading = false,
  selectedRows = new Set(),
  onRowSelect,
  onRowsSelect,
  
  // Pagination
  pagination,
  
  // Sorting
  sortConfig,
  onSort,
  
  // Filtering  
  filters = {},
  onFilter,
  
  // Search
  searchValue = '',
  onSearch,
  
  // Row ID
  getRowId = (item, index) => index,
  
  // Actions
  rowActions = [],
  
  // Styling
  variant = 'default',
  size = 'md',
  className
}, ref) => {
  
  // BIRHAUS: State management
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set())
  const [searchTerm, setSearchTerm] = useState(searchValue)
  const [localSortConfig, setLocalSortConfig] = useState(sortConfig)
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const resolveText = (spanish?: string, english?: string, fallback?: string) => {
    return spanish || english || fallback || ''
  }
  
  const displayEmptyMessage = resolveText(
    emptyMessageEs, 
    emptyMessageEn, 
    emptyMessage || 'No hay datos disponibles'
  )
  
  const displayLoadingMessage = resolveText(
    loadingMessageEs,
    loadingMessageEn,
    loadingMessage || 'Cargando datos...'
  )
  
  // BIRHAUS: Calculate cognitive load and visible columns
  const { visibleColumns, hiddenColumnCount, cognitiveLoad } = useMemo(() => {
    const priorityOrder = { high: 1, medium: 2, low: 3 }
    
    // Sort columns by priority and cognitive weight
    const sortedColumns = [...columns].sort((a, b) => {
      const aPriority = priorityOrder[a.priority || 'medium']
      const bPriority = priorityOrder[b.priority || 'medium']
      if (aPriority !== bPriority) return aPriority - bPriority
      
      const aWeight = a.cognitiveWeight || 5
      const bWeight = b.cognitiveWeight || 5
      return aWeight - bWeight
    })
    
    const visible = sortedColumns
      .filter(col => !hiddenColumns.has(col.id))
      .slice(0, maxVisibleColumns)
    
    const hidden = sortedColumns.length - visible.length
    const totalCognitiveWeight = visible.reduce((sum, col) => sum + (col.cognitiveWeight || 5), 0)
    const avgCognitiveWeight = totalCognitiveWeight / visible.length
    
    const load = visible.length > 7 ? 'high' : 
                 visible.length > 5 ? 'medium' : 'low'
    
    return {
      visibleColumns: visible,
      hiddenColumnCount: hidden,
      cognitiveLoad: load,
      avgCognitiveWeight
    }
  }, [columns, hiddenColumns, maxVisibleColumns])
  
  // BIRHAUS: Warn about cognitive overload
  if (showCognitiveWarning && visibleColumns.length > 7) {
    console.warn(`BIRHAUS Miller's Law Warning: Table shows ${visibleColumns.length} columns (recommended: max 7)`)
  }
  
  // BIRHAUS: Handle sorting
  const handleSort = (columnId: string) => {
    const column = columns.find(col => col.id === columnId)
    if (!column?.sortable) return
    
    const direction: 'asc' | 'desc' = localSortConfig?.key === columnId && localSortConfig.direction === 'asc' 
      ? 'desc' : 'asc'
    
    const newSortConfig = { key: columnId, direction }
    setLocalSortConfig(newSortConfig)
    onSort?.(columnId, direction)
  }
  
  // BIRHAUS: Handle row selection
  const handleRowSelect = (rowId: string | number, selected: boolean) => {
    onRowSelect?.(rowId, selected)
  }
  
  const handleSelectAll = (selected: boolean) => {
    const allIds = data.map((item, index) => getRowId(item, index))
    onRowsSelect?.(allIds, selected)
  }
  
  // BIRHAUS: Get cell value
  const getCellValue = (item: any, column: BirhausTableColumn) => {
    if (typeof column.accessor === 'function') {
      return column.accessor(item)
    }
    return item[column.accessor]
  }
  
  // BIRHAUS: Format cell value
  const formatCellValue = (value: any, column: BirhausTableColumn, item: any) => {
    if (column.cell) {
      return column.cell(value, item)
    }
    
    if (column.isCurrency && typeof value === 'number') {
      return new Intl.NumberFormat('es-PY', {
        style: 'currency',
        currency: 'PYG',
        minimumFractionDigits: 0
      }).format(value)
    }
    
    if (column.isPercentage && typeof value === 'number') {
      return `${(value * 100).toFixed(1)}%`
    }
    
    if (column.isDate && value) {
      return new Date(value).toLocaleDateString('es-ES')
    }
    
    return value?.toString() || ''
  }
  
  // BIRHAUS Principle #9: Consistency via tokens
  const sizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  }
  
  const variantClasses = {
    default: 'border border-gray-200',
    striped: 'border border-gray-200',
    bordered: 'border-2 border-gray-300',
    minimal: 'border-0'
  }
  
  if (loading) {
    return (
      <div className="w-full">
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-gray-200 rounded"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-8 bg-gray-100 rounded"></div>
          ))}
        </div>
        <p className="text-center text-gray-500 mt-4">{displayLoadingMessage}</p>
      </div>
    )
  }
  
  if (data.length === 0) {
    return (
      <div className="w-full text-center py-12">
        <p className="text-gray-500 text-lg">{displayEmptyMessage}</p>
      </div>
    )
  }
  
  return (
    <div className="w-full space-y-4">
      {/* BIRHAUS: Cognitive load warning */}
      {showCognitiveWarning && cognitiveLoad === 'high' && (
        <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <span className="text-sm text-yellow-800">
            Mostrando {visibleColumns.length} columnas (máximo recomendado: 7). 
            {hiddenColumnCount > 0 && ` ${hiddenColumnCount} columnas ocultas.`}
          </span>
        </div>
      )}
      
      {/* Table controls */}
      {(searchable || filterable || hiddenColumnCount > 0) && (
        <div className="flex items-center justify-between gap-4">
          {/* Search */}
          {searchable && (
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  onSearch?.(e.target.value)
                }}
                placeholder="Buscar..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          )}
          
          {/* Column visibility toggle */}
          {hiddenColumnCount > 0 && (
            <button
              onClick={() => setHiddenColumns(new Set())}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <MoreHorizontal className="h-4 w-4" />
              Mostrar {hiddenColumnCount} columnas más
            </button>
          )}
        </div>
      )}
      
      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table 
          ref={ref}
          className={cn(
            'w-full table-auto',
            sizeClasses[size],
            variantClasses[variant],
            className
          )}
        >
          {/* Header */}
          <thead className="bg-gray-50">
            <tr>
              {selectable && (
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
              )}
              
              {visibleColumns.map((column) => (
                <th
                  key={column.id}
                  className={cn(
                    'px-4 py-3 font-medium text-gray-900',
                    column.align === 'center' && 'text-center',
                    column.align === 'right' && 'text-right',
                    column.sortable && 'cursor-pointer hover:bg-gray-100'
                  )}
                  style={{ width: column.width }}
                  onClick={() => column.sortable && handleSort(column.id)}
                >
                  <div className="flex items-center gap-2">
                    <span>{column.headerEs || column.header}</span>
                    {column.sortable && localSortConfig?.key === column.id && (
                      localSortConfig.direction === 'asc' ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )
                    )}
                  </div>
                </th>
              ))}
              
              {rowActions.length > 0 && (
                <th className="px-4 py-3 text-right font-medium text-gray-900">
                  Acciones
                </th>
              )}
            </tr>
          </thead>
          
          {/* Body */}
          <tbody className="divide-y divide-gray-200">
            {data.map((item, index) => {
              const rowId = getRowId(item, index)
              const isSelected = selectedRows.has(rowId)
              
              return (
                <tr
                  key={rowId}
                  className={cn(
                    'hover:bg-gray-50',
                    variant === 'striped' && index % 2 === 1 && 'bg-gray-50',
                    isSelected && 'bg-blue-50'
                  )}
                >
                  {selectable && (
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => handleRowSelect(rowId, e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                  )}
                  
                  {visibleColumns.map((column) => {
                    const value = getCellValue(item, column)
                    const formattedValue = formatCellValue(value, column, item)
                    
                    return (
                      <td
                        key={column.id}
                        className={cn(
                          'px-4 py-3 text-gray-900',
                          column.align === 'center' && 'text-center',
                          column.align === 'right' && 'text-right',
                          column.isCurrency && 'font-mono'
                        )}
                      >
                        {formattedValue}
                      </td>
                    )
                  })}
                  
                  {rowActions.length > 0 && (
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {rowActions.map((action) => (
                          <button
                            key={action.id}
                            onClick={() => action.onClick(item)}
                            disabled={action.disabled?.(item)}
                            className={cn(
                              'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded',
                              'focus:outline-none focus:ring-2 focus:ring-offset-1',
                              'disabled:pointer-events-none disabled:opacity-50',
                              action.variant === 'primary' && 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
                              action.variant === 'destructive' && 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
                              (!action.variant || action.variant === 'secondary') && 'bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500'
                            )}
                          >
                            {action.icon}
                            {action.labelEs || action.label}
                          </button>
                        ))}
                      </div>
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      {pagination && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Mostrando {((pagination.page - 1) * pagination.pageSize) + 1} a{' '}
            {Math.min(pagination.page * pagination.pageSize, pagination.total)} de{' '}
            {pagination.total} resultados
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => pagination.onPageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              Anterior
            </button>
            
            <span className="px-3 py-1 text-sm">
              {pagination.page}
            </span>
            
            <button
              onClick={() => pagination.onPageChange(pagination.page + 1)}
              disabled={pagination.page * pagination.pageSize >= pagination.total}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  )
})

BirhausTable.displayName = 'BirhausTable'

// BIRHAUS: Hook for managing table state
export function useBirhausTable<T>(initialData: T[] = []) {
  const [data, setData] = useState<T[]>(initialData)
  const [selectedRows, setSelectedRows] = useState<Set<string | number>>(new Set())
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | undefined>()
  const [filters, setFilters] = useState<Record<string, any>>({})
  const [searchValue, setSearchValue] = useState('')
  
  const handleRowSelect = (id: string | number, selected: boolean) => {
    const newSelected = new Set(selectedRows)
    if (selected) {
      newSelected.add(id)
    } else {
      newSelected.delete(id)
    }
    setSelectedRows(newSelected)
  }
  
  const handleRowsSelect = (ids: (string | number)[], selected: boolean) => {
    const newSelected = new Set(selectedRows)
    ids.forEach(id => {
      if (selected) {
        newSelected.add(id)
      } else {
        newSelected.delete(id)
      }
    })
    setSelectedRows(newSelected)
  }
  
  const clearSelection = () => setSelectedRows(new Set())
  
  return {
    data,
    setData,
    selectedRows,
    setSelectedRows,
    sortConfig,
    setSortConfig,
    filters,
    setFilters,
    searchValue,
    setSearchValue,
    handleRowSelect,
    handleRowsSelect,
    clearSelection
  }
}

// Components and utilities are exported via individual export declarations above